import React, { useState } from 'react';
import {
  Button,
  Alert,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { SidePanelContent, SidePanelFooter } from '@securspace/securspace-ui-kit';
import { createApiToken } from '../api-integration-requests';
import { ApiCredentials, CopiedType, INITIAL_FORM_DATA } from '../types';
import { useTokenForm } from '../hooks/useTokenForm';
import TokenFormFields from './TokenFormFields';
import CredentialsDisplay from './CredentialsDisplay';
import { getErrorMessageForStandardResponse } from '../../../util/NetworkErrorUtil';

interface CreateTokenFormProps {
  onTokenCreated?: () => void;
  onClose: (forceReset?: boolean) => void;
  onCredentialsCreated: (credentials: ApiCredentials) => void;
  onSavedConfirmedChange: (confirmed: boolean) => void;
  onFormDirtyChange: (isDirty: boolean) => void;
  savedConfirmed: boolean;
  credentials: ApiCredentials | null;
}

/**
 * Form component for creating API tokens.
 * Handles form state, validation, submission, and displays credentials on success.
 */
const CreateTokenForm: React.FC<CreateTokenFormProps> = ({
  onTokenCreated,
  onClose,
  onCredentialsCreated,
  onSavedConfirmedChange,
  onFormDirtyChange,
  savedConfirmed,
  credentials,
}) => {
  // Form state using shared hook
  const {
    formData,
    emailErrorMsg,
    firstNameErrorMsg,
    lastNameErrorMsg,
    phoneNumberErrorMsg,
    isFormValid,
    handleFieldChange,
    handlePhoneNumberChange,
    resetForm,
    getSubmitPayload,
  } = useTokenForm({
    initialData: INITIAL_FORM_DATA,
    onDirtyChange: onFormDirtyChange,
  });

  // Submission state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // UI state for credentials display
  const [copiedItem, setCopiedItem] = useState<CopiedType>('none');

  const handleCopyToClipboard = async (text: string, type: CopiedType) => {
    if (type === 'none') return;

    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(type);
      setTimeout(() => setCopiedItem('none'), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyBoth = () => {
    if (credentials) {
      const bothText = `API Key: ${credentials.key}\nSecret: ${credentials.secret}`;
      handleCopyToClipboard(bothText, 'both');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isFormValid) return;

    setLoading(true);
    setError(null);

    try {
      const response = await createApiToken(getSubmitPayload());
      onCredentialsCreated(response.body);
      onTokenCreated?.();
    } catch (err: unknown) {
      const errorMessage = getErrorMessageForStandardResponse(err, 'Failed to create API token. Please try again.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    resetForm();
    setError(null);
    setCopiedItem('none');
    onFormDirtyChange(false);
    onClose(true);
  };

  const isSuccess = credentials !== null;

  return (
    <>
      {/* Content */}
      <SidePanelContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isSuccess ? (
          <CredentialsDisplay
            credentials={credentials}
            copiedItem={copiedItem}
            onCopy={handleCopyToClipboard}
            onCopyBoth={handleCopyBoth}
            savedConfirmed={savedConfirmed}
            onSavedConfirmedChange={onSavedConfirmedChange}
          />
        ) : (
          <form onSubmit={handleSubmit} noValidate>
            <TokenFormFields
              formData={formData}
              loading={loading}
              emailErrorMsg={emailErrorMsg}
              firstNameErrorMsg={firstNameErrorMsg}
              lastNameErrorMsg={lastNameErrorMsg}
              phoneNumberErrorMsg={phoneNumberErrorMsg}
              onFieldChange={handleFieldChange}
              onPhoneNumberChange={handlePhoneNumberChange}
            />
          </form>
        )}
      </SidePanelContent>

      {/* Footer */}
      <SidePanelFooter>
        {isSuccess ? (
          <Tooltip title={!savedConfirmed ? "Please confirm you've saved your credentials" : ""} placement="top">
            <span style={{ display: 'block', width: '100%' }}>
              <Button
                onClick={handleDone}
                variant={'contained'}
                color={'primary'}
                fullWidth
                disabled={!savedConfirmed}
                sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }}
              >
                Done
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            onClick={handleSubmit}
            variant={'contained'}
            color={'primary'}
            disabled={loading || !isFormValid}
            fullWidth
            sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }}
          >
            {loading ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        )}
      </SidePanelFooter>
    </>
  );
};

export default CreateTokenForm;
